import talk2Api from 'src/plugins/api/api'
import { AGENT_BUSY_STATUSES } from 'src/constants/agent-status'

const BLOCKED_RECHECK_INTERVAL_MS = 30000

// Replays the server's agent status through the agent_status_updated event, since a
// missed push is never redelivered. A component overrides isAgentBusyBlockShown().
export default {
  mounted () {
    this.startAgentStatusReconcile()
  },

  beforeDestroy () {
    this.stopAgentStatusReconcile()
  },

  methods: {
    isAgentBusyBlockShown () {
      return false
    },

    reconcileAgentStatus (trigger) {
      if (this.$options.agentStatusReconcileRequest) {
        return this.$options.agentStatusReconcileRequest
      }

      const statusAtRequest = this.profile?.agent_status
      const request = talk2Api.V1.profile.getAgentStatus()
        .then(res => this.applyServerAgentStatus(res?.data?.agent_status, statusAtRequest, trigger))
        .catch(err => {
          console.warn('[Agent status reconcile] request failed', { trigger, err })
          return null
        })
        .finally(() => {
          this.$options.agentStatusReconcileRequest = null
        })

      this.$options.agentStatusReconcileRequest = request
      return request
    },

    applyServerAgentStatus (serverStatus, statusAtRequest, trigger) {
      if (serverStatus === undefined || serverStatus === null || !this.profile) {
        return null
      }

      const localStatus = this.profile.agent_status

      // A push landed while the request was in flight and is newer than this answer.
      if (localStatus !== statusAtRequest) {
        return localStatus
      }

      const blockShown = this.isAgentBusyBlockShown()

      // A matching status still has to be replayed while a block is up: the
      // Salesforce block is its own flag and can outlive the status that set it.
      if (serverStatus === localStatus && (!blockShown || AGENT_BUSY_STATUSES.includes(serverStatus))) {
        return serverStatus
      }

      this.$Sentry.captureMessage('CRM widget agent status diverged from server', {
        level: 'warning',
        fingerprint: ['crm-widget-agent-status-divergence'],
        tags: {
          company_id: this.currentCompany?.id ?? null,
          user_id: this.profile.id,
          trigger,
          widget: this.$route?.name ?? null
        },
        extra: {
          local_status: localStatus,
          server_status: serverStatus,
          block_shown: blockShown
        }
      })

      this.$VueEvent.fire('agent_status_updated', {
        user_id: this.profile.id,
        company_id: this.currentCompany?.id,
        agent_status: serverStatus
      })

      return serverStatus
    },

    // Only when the dial could be refused, so an ordinary dial costs no request.
    async reconcileAgentStatusBeforeDial () {
      if (AGENT_BUSY_STATUSES.includes(this.profile?.agent_status) || this.isAgentBusyBlockShown()) {
        await this.reconcileAgentStatus('dial')
      }
    },

    handleAgentBusyBlockChanged (shown) {
      clearInterval(this.$options.agentBusyBlockRecheck)
      this.$options.agentBusyBlockRecheck = null

      if (shown) {
        this.$options.agentBusyBlockRecheck = setInterval(() => {
          this.reconcileAgentStatus('blocked')
        }, BLOCKED_RECHECK_INTERVAL_MS)
      }
    },

    startAgentStatusReconcile () {
      this.$options.agentStatusOnReconnect = () => this.reconcileAgentStatus('reconnect')
      this.$VueEvent.listen('broadcast_reconnected', this.$options.agentStatusOnReconnect)

      this.$options.agentStatusOnVisibility = () => {
        if (document.visibilityState === 'visible') {
          this.reconcileAgentStatus('visibility')
        }
      }
      document.addEventListener('visibilitychange', this.$options.agentStatusOnVisibility)

      this.$options.agentBusyBlockUnwatch = this.$watch(
        () => this.isAgentBusyBlockShown(),
        shown => this.handleAgentBusyBlockChanged(shown)
      )
    },

    stopAgentStatusReconcile () {
      this.$VueEvent.stop('broadcast_reconnected', this.$options.agentStatusOnReconnect)
      document.removeEventListener('visibilitychange', this.$options.agentStatusOnVisibility)

      if (this.$options.agentBusyBlockUnwatch) {
        this.$options.agentBusyBlockUnwatch()
      }
      this.handleAgentBusyBlockChanged(false)
    }
  }
}
